import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from "react-redux"
import { Button } from '../../Components/UI/button';
import { Card, CardContent } from '../../Components/UI/card';
import { Input } from '../../Components/UI/input';
import { cn } from '../../Lib/Cn';
import { User, Database, Shield, Puzzle, Globe, TrendingUp, ChevronRight, LogOut, ArrowLeft, ReceiptText, AlertTriangle, Lightbulb, Building2, TimerReset, RefreshCw, Package, Building, MapPin, UserPlus, ThumbsDown, CircleX, Archive, MapPinned, Edit3, Columns3Cog, Target, ListOrdered, RotateCcw, Bug, Search, X } from 'lucide-react';
import { isMobileDevice, isIOSDevice, openInExternalBrowser, getThreadModeTitle, isLive } from '../../Utils/Utilities';
import { APP_VERSION } from '../../Utils/Constants';
import { clearEverything } from '../../Utils/CookieHandler';
import UsersPage from './SubSettings/Users';
import MastersPage from './SubSettings/Masters';
import PoliciesPage from './SubSettings/Policies';
import IntegrationsPage from './SubSettings/Integrations';
import Upcoming from '../../Components/Common/Upcoming';
import SubscriptionInfo from './SubscriptionInfo/SubscriptionInfo';
import UpgradeSubscription from './UpgradeSubscription/UpgradeSubscription';
import { useNavigate, useLocation } from 'react-router-dom';
import { updateHeaderConfig, userIsAdmin, userIsCreator, isSmartSellerMode, getActiveThreadTypesArr } from "../../Store/Reducers";
import { useDesktop } from "../../Components/Common/IsDesktop";
import ProfileSection from './Profile/ProfileSection';
import MastersDialog from './Masters';
import OrderStagesDialog from './OrderStagesDialog';
import { getSubscriptionData } from './SubscriptionInfo/SubscriptionInfoAPI';
import WebStoreConfig from './WebStoreConfig/WebStoreConfig';
import { AlertBox } from '../../Components/Common/AlertBox';
import apiService from '../../Services/APIService';
import { getAuthData } from '../../Utils/AuthUtils';
import ResetDataDialog from './ResetData/ResetDataDialog';
import GoalsSection from './Profile/GoalsSection';
import { SHOW_BUSINESS_GOALS, canShowImportInquiries } from '../../Utils/FeatureFlags';
import useIsMobile from '../../Hooks/useIsMobile';
import DashboardHeader from '../Dashboard/Fragments/DashboardHeader';
import MobileAlertDrawer from '../../Components/Common/MobileAlertDrawer';
import TktDialog from '../Ticket/TktDialog';

const showImportInquiries = canShowImportInquiries();

const Setting = () => {
  const { data: agencyData } = useSelector(state => state.agencyData);
  const isAdmin = useSelector(userIsAdmin);
  const isCreatorUser = useSelector(userIsCreator);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isDesktop = useDesktop();
  const isMobile = useIsMobile();
  const [currentPage, setCurrentPage] = useState('main');
  const [showUpcoming, setShowUpcoming] = useState(false);
  const [openModel, setOpenModel] = useState({ modelID: '' });
  const [showIOSAlert, setShowIOSAlert] = useState(false);
  const [showLogoutAlert, setShowLogoutAlert] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [selectedFeedbackType, setSelectedFeedbackType] = useState(null);

  // Search state
  const [showSearch, setShowSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef(null);

  const [hasInvoices, setHasInvoices] = useState(false);
  const [masterDialogOpen, setMasterDialogOpen] = useState(false);
  const [currentMasterType, setCurrentMasterType] = useState(null);
  const [orderStagesOpen, setOrderStagesOpen] = useState(false);

  const masterData = useSelector(state => state.masterData) || {};
  const recordTypes = masterData.recordTypes || [];
  const regions = masterData.regions || [];
  const connExtraFields = masterData.connExtraFields || [];
  const thrExtraFields = masterData.thrExtraFields || [];

  const checkIfTaskTypesChanged = (recordTypes) => {
    if (!recordTypes || recordTypes.length !== 8) return true;
    const defaults = [
      { TID: 1, Typ: "Visit", IsInxn: true, HasGeo: true },
      { TID: 2, Typ: "Call", IsInxn: true, HasGeo: false },
      { TID: 3, Typ: "Meeting", IsInxn: true, HasGeo: false },
      { TID: 4, Typ: "Email", IsInxn: true, HasGeo: false },
      { TID: 5, Typ: "Log", IsInxn: false, HasGeo: false },
      { TID: 6, Typ: "Follow-up", IsInxn: true, HasGeo: false },
      { TID: 7, Typ: "Message", IsInxn: true, HasGeo: false },
      { TID: 8, Typ: "Internal", IsInxn: false, HasGeo: false }
    ];
    for (let i = 0; i < defaults.length; i++) {
      const d = defaults[i];
      const match = recordTypes.find(rt => Number(rt.TID) === d.TID);
      if (!match) return true;
      if (match.Typ !== d.Typ) return true;
      if (Boolean(match.IsInxn) !== d.IsInxn) return true;
      if (Boolean(match.HasGeo) !== d.HasGeo) return true;
    }
    return false;
  };
  const hasExtraTaskTypes = checkIfTaskTypesChanged(recordTypes);
  const hasMultipleRegions = regions.length > 1;
  const hasConnExtraFields = connExtraFields.length > 0;
  const hasThrExtraFields = thrExtraFields.length > 0;

  const threadTypeArr = useSelector(getActiveThreadTypesArr);
  const currActiveThreadType = getThreadModeTitle(threadTypeArr);

  useEffect(() => {
    const checkInvoices = async () => {
      try {
        const data = await getSubscriptionData();
        if (data?.BZInvoices) {
          const invoices = JSON.parse(data.BZInvoices) || [];
          setHasInvoices(invoices.length > 0);
        }
      } catch (error) {
        console.error("Failed to check subscription invoices:", error);
      }
    };
    checkInvoices();
  }, [hasInvoices, isMobile, recordTypes]);

  // Mobile header event listeners for search toggle
  useEffect(() => {
    const handleToggleSearch = () => {
      setShowSearch(prev => {
        const next = !prev;
        if (!next) setSearchTerm('');
        return next;
      });
    };
    window.addEventListener('settings-toggle-search', handleToggleSearch);
    return () => window.removeEventListener('settings-toggle-search', handleToggleSearch);
  }, []);

  // Auto focus mobile search input when opened
  useEffect(() => {
    if (showSearch && searchInputRef.current) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [showSearch]);

  const settingsConfig = useMemo(() => [
    {
      id: 'systemConfiguration',
      title: 'Account',
      showInMobile: true,
      showInDesktop: true,
      subSettings: [
        {
          id: 'users',
          icon: User,
          title: 'Users',
          description: 'Manage system users and roles',
          tags: ['user', 'team', 'member', 'employee', 'staff', 'role', 'admin', 'permission', 'access', 'executive', 'salesperson', 'login', 'account'],
          showInMobile: !isSmartSellerMode(),
          showInDesktop: !isSmartSellerMode(),
          isComponent: false
        },
        {
          id: 'profile',
          icon: Building2,
          title: 'Profile',
          description: 'Set your company profile',
          tags: ['company', 'business', 'logo', 'details', 'name', 'address', 'gst', 'phone', 'contact', 'email', 'website', 'organization', 'branding'],
          showInMobile: isCreatorUser,
          showInDesktop: isCreatorUser,
          isComponent: false
        },
        {
          id: 'webstoreConfig',
          icon: Globe,
          title: 'Webstore',
          description: 'Settings for your webstore',
          tags: ['shop', 'online store', 'ecommerce', 'catalog', 'domain', 'website', 'logo', 'link', 'products', 'storefront', 'payment', 'cart'],
          showInMobile: true,
          showInDesktop: true,
          isComponent: false
        },
        {
          id: 'upgrade',
          icon: TrendingUp,
          title: isIOSDevice() ? 'Manage Subscription' : 'Upgrade',
          description: isIOSDevice() ? 'Manage your subscription' : 'Upgrade your subscription',
          tags: ['plan', 'pricing', 'subscription', 'premium', 'billing', 'invoice', 'pro', 'payment', 'upgrade', 'renewal', 'license'],
          showInMobile: !isIOSDevice(),
          showInDesktop: true,
          isComponent: false
        },
        {
          id: 'subscriptionInfo',
          icon: ReceiptText,
          title: 'Subscription',
          description: 'See all your subscription history',
          tags: ['invoice', 'receipt', 'bill', 'payment history', 'transactions', 'billing', 'plan', 'expiry', 'validity'],
          showInMobile: hasInvoices,
          showInDesktop: true,
          isComponent: false
        },
        {
          id: 'resetData',
          icon: RotateCcw,
          title: 'Reset Data',
          description: 'Delete all data and start fresh',
          tags: ['delete', 'clear', 'wipe', 'erase', 'fresh', 'restart', 'remove', 'factory reset', 'purge'],
          showInMobile: false,
          showInDesktop: isCreatorUser,
          isComponent: false
        },
        {
          id: 'goals',
          icon: Target,
          title: 'Goals',
          description: 'Set your monthly, quarterly, and yearly goals',
          tags: ['target', 'quota', 'kpi', 'revenue target', 'sales goal', 'performance', 'monthly', 'quarterly', 'yearly', 'forecast'],
          showInMobile: SHOW_BUSINESS_GOALS,
          showInDesktop: SHOW_BUSINESS_GOALS,
          isComponent: false
        }
      ]
    },
    {
      id: 'masters',
      title: 'Masters',
      showInMobile: true,
      showInDesktop: true,
      subSettings: [
        {
          id: 'products',
          icon: Package,
          title: 'Offerings',
          description: 'Maintain your catalog of offerings available for inquiries, orders and support tickets.',
          tags: ['product', 'service', 'item', 'catalog', 'sku', 'goods', 'price', 'pricing', 'rate', 'unit', 'inventory', 'offerings', 'packages'],
          showInMobile: true,
          showInDesktop: true,
          isComponent: false
        },
        {
          id: 'segments',
          icon: Building,
          title: 'Customer Categories',
          description: 'Categories contacts based on predefined segments.',
          tags: ['segment', 'category', 'group', 'classification', 'customer type', 'b2b', 'b2c', 'retail', 'wholesale', 'tag', 'types'],
          showInMobile: true,
          showInDesktop: true,
          isComponent: false
        },
        {
          id: 'cities',
          icon: MapPin,
          title: 'Cities',
          description: 'Add or update cities used across your CRM for addresses and regions.',
          tags: ['location', 'city', 'town', 'address', 'place', 'state', 'geography', 'area', 'district'],
          showInMobile: true,
          showInDesktop: true,
          isComponent: false
        },
        {
          id: 'sources',
          icon: UserPlus,
          title: 'Lead Sources',
          description: 'Define and manage the channels from which inquiries are generated.',
          tags: ['source', 'channel', 'campaign', 'referral', 'facebook', 'google', 'instagram', 'website', 'inquiry source', 'lead generation', 'marketing'],
          showInMobile: true,
          showInDesktop: true,
          isComponent: false
        },
        {
          id: 'rejectionReasons',
          icon: ThumbsDown,
          title: 'Rejection Reasons',
          description: 'Track reasons why contacts reject your inquiries or proposals.',
          tags: ['reject', 'lost', 'loss reason', 'deal lost', 'drop', 'decline', 'expensive', 'competitor', 'not interested', 'reasons'],
          showInMobile: true,
          showInDesktop: true,
          isComponent: false
        },
        {
          id: 'disqlReasons',
          icon: CircleX,
          title: 'Disqualification Reasons',
          description: 'Track reasons why connections disqualify your inquiries or proposals.',
          tags: ['disqualify', 'junk', 'invalid', 'spam', 'fake', 'not eligible', 'unqualified', 'wrong number', 'duplicate', 'disqualification'],
          showInMobile: true,
          showInDesktop: true,
          isComponent: false
        },
        {
          id: 'recordTypes',
          icon: Archive,
          title: 'Task Types',
          description: 'Categorize different types of records and tasks within the system.',
          tags: ['task', 'activity', 'call', 'meeting', 'visit', 'follow up', 'email', 'message', 'log', 'interaction', 'todo', 'task types'],
          showInMobile: hasExtraTaskTypes,
          showInDesktop: true,
          isComponent: false
        },
        {
          id: 'regions',
          icon: MapPinned,
          title: 'Sales Regions',
          description: 'Define and manage regions for region wise management of contacts.',
          tags: ['region', 'territory', 'zone', 'area', 'sales zone', 'geographic region', 'state', 'branch', 'sales regions'],
          showInMobile: hasMultipleRegions,
          showInDesktop: true,
          isComponent: false
        },
        {
          id: 'extraFields',
          icon: Edit3,
          title: 'Extra Fields',
          description: 'Configure additional fields for contacts and inquiries.',
          tags: ['custom fields', 'additional fields', 'extra fields', 'contacts', 'inquiries', 'inquiry', 'leads', 'inputs', 'fields', 'more fields'],
          showInMobile: hasConnExtraFields || hasThrExtraFields,
          showInDesktop: true,
          isComponent: false
        },
        {
          id: 'threadStages',
          icon: Columns3Cog,
          title: `${currActiveThreadType?.typSng || 'Inquiry'} Stages`,
          description: `Change the stages as per your requirement.`,
          tags: ['pipeline', 'funnel', 'stages', 'inquiry status', 'progress', 'milestone', 'lead stages', 'process', 'workflow', 'kanban'],
          showInMobile: true,
          showInDesktop: true,
          isComponent: false
        },
        {
          id: 'orderStages',
          icon: ListOrdered,
          title: 'Order Stages',
          description: 'Add or remove the stages an order moves through.',
          tags: ['orders', 'tracking', 'sales pipeline', 'order status', 'delivery', 'dispatch', 'processing', 'fulfillment', 'workflow', 'stages'],
          showInMobile: true,
          showInDesktop: true,
          isComponent: false
        },
        {
          id: 'integrations',
          icon: Puzzle,
          title: 'Integrations',
          description: 'Connect external platforms',
          tags: ['api', 'webhook', 'indiamart', 'tradeindia', 'justdial', 'facebook', 'meta', 'whatsapp', 'zapier', 'sync', 'connect', 'import'],
          showInMobile: canShowImportInquiries(),
          showInDesktop: false,
          isComponent: false
        }
      ]
    },
    {
      id: 'policies',
      title: 'Policies',
      showInMobile: true,
      showInDesktop: true,
      subSettings: [
        {
          id: 'policies',
          icon: Shield,
          title: 'Policies',
          description: 'Set business rules and controls',
          tags: ['policy', 'rules', 'controls', 'duplication', 'duplicate', 'leads', 'language', 'recording', 'transcribe', 'voice', 'audio', 'business rules'],
          component: PoliciesPage,
          showInMobile: true,
          showInDesktop: true,
          isComponent: true
        }
      ]
    },
    {
      id: 'integrations',
      title: 'Integrations',
      showInMobile: false,
      showInDesktop: canShowImportInquiries(),
      subSettings: [
        {
          id: 'integrations',
          icon: Puzzle,
          title: 'Integrations',
          description: 'Connect external platforms',
          tags: ['api', 'webhook', 'indiamart', 'tradeindia', 'justdial', 'facebook', 'meta', 'whatsapp', 'zapier', 'sync', 'connect', 'import', 'leads import'],
          component: IntegrationsPage,
          showInMobile: canShowImportInquiries(),
          showInDesktop: canShowImportInquiries(),
          isComponent: true
        }
      ]
    }
  ], [isSmartSellerMode, isCreatorUser, isIOSDevice, hasInvoices, hasExtraTaskTypes, hasMultipleRegions, hasConnExtraFields, hasThrExtraFields, currActiveThreadType, canShowImportInquiries]);

  const moreItems = useMemo(() => [
    {
      id: 'shareIdea',
      title: 'Share Idea',
      icon: Lightbulb,
      onClick: () => handleFeedbackClick(2),
      isDestructive: false,
      show: true,
      tags: ['feedback', 'suggestion', 'feature', 'request', 'idea', 'improve', 'support', 'help']
    },
    {
      id: 'reportBug',
      title: 'Report Bug',
      icon: AlertTriangle,
      onClick: () => handleFeedbackClick(1),
      isDestructive: false,
      show: true,
      tags: ['issue', 'bug', 'error', 'problem', 'defect', 'technical', 'support', 'broken', 'help', 'alert']
    },
    {
      id: 'resetData',
      title: 'Reset Data',
      icon: RotateCcw,
      onClick: () => setOpenModel({ modelID: 'resetData' }),
      isDestructive: true,
      show: isCreatorUser,
      tags: ['delete', 'clear', 'wipe', 'erase', 'fresh', 'restart', 'remove', 'factory reset', 'purge']
    },
    {
      id: 'logout',
      title: 'Log out',
      icon: LogOut,
      onClick: () => setShowLogoutAlert(true),
      isDestructive: true,
      show: true,
      tags: ['signout', 'exit', 'leave', 'disconnect', 'logout']
    }
  ], [isCreatorUser]);

  const query = searchTerm.toLowerCase().trim();

  const isMatch = (item) => {
    if (!query) return true;
    const titleMatch = item.title?.toLowerCase().includes(query);
    const descMatch = item.description?.toLowerCase().includes(query);
    const tagsMatch = item.tags?.some(tag => tag.toLowerCase().includes(query) || query.includes(tag.toLowerCase()));
    return Boolean(titleMatch || descMatch || tagsMatch);
  };

  const filteredSettingsConfig = useMemo(() => {
    return settingsConfig.map((category) => {
      const isCategoryVisible = isMobile
        ? (category.showInMobile !== undefined ? category.showInMobile : (category.show !== undefined ? category.show : true))
        : (category.showInDesktop !== undefined ? category.showInDesktop : (category.show !== undefined ? category.show : true));

      if (!isCategoryVisible) return null;

      const visibleSubSettings = category.subSettings?.filter((subSetting) => {
        const isSubVisible = isMobile
          ? (subSetting.showInMobile !== undefined ? subSetting.showInMobile : (subSetting.show !== undefined ? subSetting.show : true))
          : (subSetting.showInDesktop !== undefined ? subSetting.showInDesktop : (subSetting.show !== undefined ? subSetting.show : true));
        if (!isSubVisible) return false;
        return isMatch(subSetting);
      }) || [];

      if (visibleSubSettings.length === 0) return null;

      return {
        ...category,
        subSettings: visibleSubSettings
      };
    }).filter(Boolean);
  }, [settingsConfig, query, isMobile]);

  const visibleMoreItems = useMemo(() => {
    return moreItems.filter(item => item.show && isMatch(item));
  }, [moreItems, query]);

  const location = useLocation();

  useEffect(() => {
    if (!location.hash) {
      setCurrentPage('main');
    }
  }, [location.key]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const openParam = params.get('open');
    if (openParam) {
      setOpenModel({ modelID: openParam });
      navigate(location.pathname, { replace: true });
    }
  }, [location.search]);

  // Handles browser back button navigation
  useEffect(() => {
    const hash = window.location.hash;
    if (hash === '#integrations') {
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);

  const setHeaderData = () => {
    if (isMobile) return;
    let headerObj = {
      title: 'Settings',
      iconLeft: {
        icon: ArrowLeft,
        onClick: () => navigate(-1),
      },
      actionsRight: [{
        icon: LogOut,
        style: { height: "90px", width: "90px" },
        onClick: () => handleLogout(),
      }],
    };

    dispatch(updateHeaderConfig(headerObj));
  }

  useEffect(() => {
    setHeaderData();
  }, []);

  const handleSettingClick = (settingID) => {
    const masterKeys = ['sources', 'products', 'cities', 'connExtraFields', 'thrExtraFields', 'extraFields', 'threadStages', 'recordTypes', 'rejectionReasons', 'disqlReasons', 'segments', 'regions'];
    if (masterKeys.includes(settingID)) {
      setCurrentMasterType(settingID);
      setMasterDialogOpen(true);
      return;
    }

    if (settingID === 'users') {
      navigate('/users')
      return;
    }
    else if (settingID === 'orderStages') {
      setOrderStagesOpen(true);
      return;
    }
    else if (settingID === 'webstore') {
      setShowUpcoming(true);
      return;
    }
    else if (settingID === 'subscriptionInfo') {
      setOpenModel({ modelID: 'subscriptionInfo' });
      return;
    }
    else if (settingID === 'integrations') {
      if (!isMobile) {
        setCurrentPage('integrations');
      } else {
        navigate('/integrations');
      }
      return;
    }
    else if (settingID === 'upgrade') {
      if (isIOSDevice()) {
        setShowIOSAlert(true);
        return;
      }
      setOpenModel({ modelID: 'upgrade' });
      return;
    }
    else if (settingID === 'profile') {
      setOpenModel({ modelID: 'profile' });
      return;
    }
    else if (settingID === 'webstoreConfig') {
      setOpenModel({ modelID: 'webstoreConfig' });
      return;
    }
    else if (settingID === 'resetData') {
      setOpenModel({ modelID: 'resetData' });
      return;
    }
    else if (settingID === 'goals') {
      setOpenModel({ modelID: 'goals' });
      return;
    }
  };

  const handleFeedbackClick = (type) => {
    setSelectedFeedbackType(type);
    setIsFeedbackOpen(true);
  };

  const handleFeedbackClose = () => {
    setIsFeedbackOpen(false);
    setSelectedFeedbackType(null);
  };

  const handleLogout = async () => {
    try {
      const authData = getAuthData();

      if (!authData) {
        throw new Error('No authentication data found');
      }

      const result = await apiService.accessSetData(2, 'logout', {});
      const parsedResult = typeof result.data === 'string' ? JSON.parse(result.data) : result.data;

      if (parsedResult?.Table && parsedResult.Table[0]?.FnErrCode !== '-1') {
        throw new Error(parsedResult.Table[0]?.ErrMsg || 'Logout failed');
      }
      clearEverything();

      return {
        success: true,
        message: 'Logged out.'
      };
    } catch (error) {
      console.error('Error logging out:', error);
      throw error;
    }
  };

  const renderMainSettings = () => (
    <div className="bg-bg-grey flex flex-col min-h-[calc(100vh-100px)]">
      {!isMobileDevice() && (
        <div className="flex justify-between items-center px-1 my-1 mb-8">
          <h1 className="text-3xl font-bold text-text-color web-header-name">
            Settings
          </h1>
          <div className="flex items-center gap-3">
            <div className="relative w-72 lg:w-80">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <Input
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 pr-8 h-10 w-full bg-white border-gray-200 rounded-lg text-sm focus-visible:ring-sahi-primary-color shadow-2xs"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5 rounded-full"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <Button
              onClick={handleLogout}
              className="bg-sahi-red border-sahi-red hover:text-white hover:border-sahi-red hover:text-sahi-red h-10"
            >
              <LogOut className="mr-2 h-4 w-4 md:h-5 md:w-5" />
              Log out
            </Button>
          </div>
        </div>
      )}

      {/* Mobile Search Bar */}
      {isMobile && showSearch && (
        <div className="w-full pt-2.5">
          <div className="relative w-full">
            <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <Input
              ref={searchInputRef}
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-10 h-11 w-full bg-white border-gray-200 rounded-xl text-sm focus-visible:ring-sahi-primary-color shadow-2xs"
              autoFocus
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="absolute right-3.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Empty State when no items match search */}
      {query && filteredSettingsConfig.length === 0 && (!isMobile || visibleMoreItems.length === 0) && (
        <div className="flex-1 flex flex-col items-center justify-center py-6 px-4 text-center my-auto min-h-[50vh]">
          <div className="bg-white rounded-2xl border border-gray-200/80 shadow-xs py-10 px-8 max-w-md w-full flex flex-col items-center text-center">
            <div className="relative mb-4">
              <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center text-gray-400">
                <Search className="w-7 h-7 stroke-[1.75]" />
              </div>
              <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-white shadow-xs border border-gray-200 flex items-center justify-center text-gray-400">
                <X className="w-3.5 h-3.5 stroke-[2.5]" />
              </div>
            </div>
            <h3 className="text-base font-semibold text-text-color mb-1.5">No settings found</h3>
            <p className="text-sm text-zinc-500 max-w-xs leading-relaxed mb-5">
              We couldn't find any results for <span className="font-medium text-zinc-800 break-words">"{searchTerm}"</span>. Try searching with different keywords like sources, stages, or policies.
            </p>
            <Button
              onClick={() => setSearchTerm('')}
              className="cursor-pointer"
            >
              <RotateCcw className="h-4 w-4 mr-1" />
              Clear search
            </Button>
          </div>
        </div>
      )}

      <div className={`w-full ${isMobile ? 'space-y-6 mt-3' : 'space-y-10'}`}>
        {filteredSettingsConfig.map((category) => {
          const visibleSubSettings = category.subSettings || [];

          return (
            <div key={category.id} className="space-y-4">
              {visibleSubSettings.length > 0 && (
                <div className="bg-bg-grey rounded-lg">
                  {isMobile && !query && category.id === 'systemConfiguration' && (
                    <div className="mb-6 -mx-4">
                      <DashboardHeader
                        isMobile={true}
                        dashboardHeaderData={{
                          agencyID: agencyData?.AgencyID || null,
                          agencyName: agencyData?.AgencyName || "N/A",
                          logoPath: agencyData?.Logo || "",
                          metrics: []
                        }}
                        isAdmin={isAdmin}
                        filterAdminOnly
                        navigateOnSwitch={false}
                      />
                    </div>
                  )}
                  <div className="mb-1.5 px-1">
                    <h2 className={`${isMobile ? "text-[13px] font-medium text-gray-500" : "text-2xl font-medium text-text-color pb-2 inline-block"}`}>
                      {category.title}
                    </h2>
                  </div>

                  {!isMobile ? (
                    category.subSettings.some(s => s.isComponent && (s.showInDesktop !== undefined ? s.showInDesktop : (s.show !== undefined ? s.show : true))) ? (
                      <div className="space-y-6">
                        {visibleSubSettings.map((setting) => {
                          if (!setting.isComponent) return null;
                          const SettingComponent = setting.component;
                          return (
                            <div key={setting.id} className="">
                              <SettingComponent searchTerm={searchTerm} />
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
                        {visibleSubSettings.map((setting) => {
                          const SubIconComponent = setting.icon;
                          return (
                            <Card key={setting.id} className={`sahi-card !shadow-[0_2px_6px_rgba(0,0,0,0.04)] !rounded-xl !pb-3 cursor-pointer`} onClick={() => handleSettingClick(setting.id)}>
                              <CardContent className="sahi-card-content !gap-5">
                                <div className={cn(`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 mb-1`,
                                  (setting.id === 'resetData' || setting.id === 'rejectionReasons' || setting.id === 'disqlReasons') ? 'bg-sahi-red' : 'bg-sahi-primary-color')}>
                                  <SubIconComponent className={`h-6 w-6 text-white`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h3 className={`font-semibold text-text-color ${isMobile ? "text-[14px]" : "text-lg"} mb-1 leading-tight`}>
                                    {setting.title}
                                  </h3>
                                  <p className={`text-zinc-500 ${isMobile ? "text-[12px]" : "text-sm"} leading-relaxed overflow-hidden text-ellipsis line-clamp-2`}>
                                    {setting.description}
                                  </p>
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                    )
                  ) : (
                    category.subSettings.some(s => s.isComponent && (s.showInMobile !== undefined ? s.showInMobile : (s.show !== undefined ? s.show : true))) ? (
                      <div className="space-y-6">
                        {visibleSubSettings.map((setting) => {
                          if (!setting.isComponent) return null;
                          const SettingComponent = setting.component;
                          return (
                            <div key={setting.id} className="">
                              <SettingComponent searchTerm={searchTerm} />
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="bg-white rounded-xl border border-gray-200/70 shadow-2xs overflow-hidden">
                        {visibleSubSettings.map((setting, index) => {
                          const SubIconComponent = setting.icon;
                          const isDestructive = setting.id === 'resetData';
                          return (
                            <div
                              key={setting.id}
                              className="relative flex items-center justify-between px-4 py-4 hover:bg-gray-50/70 active:bg-gray-100/70 transition-colors cursor-pointer select-none"
                              onClick={() => handleSettingClick(setting.id)}
                            >
                              <div className="flex items-center gap-3.5 min-w-0 flex-1">
                                <SubIconComponent
                                  className={cn(
                                    "w-[18px] h-[18px] stroke-[1.8] shrink-0",
                                    isDestructive ? "text-red-700" : "text-gray-700"
                                  )}
                                />
                                <div className="flex flex-col min-w-0">
                                  <span className={cn(
                                    "text-[14px] font-medium leading-tight truncate",
                                    isDestructive ? "text-red-700" : "text-gray-900"
                                  )}>
                                    {setting.title}
                                  </span>
                                </div>
                              </div>
                              <ChevronRight className="w-4 h-4 text-gray-400 stroke-[2] shrink-0 ml-2" />

                              {/* Centered non-full-width divider */}
                              {index < visibleSubSettings.length - 1 && (
                                <div className="absolute bottom-0 left-4 right-4 h-[1px] bg-gray-100" />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Mobile-only More Actions Area */}
        {isMobile && visibleMoreItems.length > 0 && (
          <div className="space-y-4">
            <div className="bg-bg-grey rounded-lg">
              <div className="mb-1.5 px-1">
                <h2 className="text-[13px] font-medium text-gray-500">
                  More
                </h2>
              </div>
              <div className="bg-white rounded-xl border border-gray-200/70 shadow-2xs overflow-hidden">
                {visibleMoreItems.map((item, index) => {
                  const ItemIcon = item.icon;
                  return (
                    <div
                      key={item.id}
                      className={cn(
                        "relative flex items-center justify-between px-4 py-4 transition-colors cursor-pointer select-none",
                        item.isDestructive
                          ? "hover:bg-red-50/40 active:bg-red-50/70"
                          : "hover:bg-gray-50/70 active:bg-gray-100/70"
                      )}
                      onClick={item.onClick}
                    >
                      <div className="flex items-center gap-3.5 min-w-0 flex-1">
                        <ItemIcon
                          className={cn(
                            "w-[18px] h-[18px] stroke-[1.8] shrink-0",
                            item.isDestructive ? "text-red-700" : "text-gray-700"
                          )}
                        />
                        <div className="flex flex-col min-w-0">
                          <span
                            className={cn(
                              "text-[14px] font-medium leading-tight truncate",
                              item.isDestructive ? "text-red-700" : "text-gray-900"
                            )}
                          >
                            {item.title}
                          </span>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400 stroke-[2] shrink-0 ml-2" />

                      {/* Centered non-full-width divider */}
                      {index < visibleMoreItems.length - 1 && (
                        <div className="absolute bottom-0 left-4 right-4 h-[1px] bg-gray-100" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* App Branding & Version Footer */}
        {isMobile && !query && (
          <div className="flex flex-col items-center justify-center pt-6 pb-6 select-none">
            <img
              src={isLive() ? "/Web/assets/Images/SahiLogo.png" : "/assets/Images/SahiLogo.png"}
              alt="SahiClub"
              className="h-6 w-auto"
            />
            <span className="text-[12px] text-gray-500 font-semibold mt-2">
              {APP_VERSION}
            </span>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {renderMainSettings()}

      <Upcoming
        open={showUpcoming}
        onOpenChange={(open) => {
          setShowUpcoming(open);
        }}
      />

      <SubscriptionInfo
        open={openModel.modelID === 'subscriptionInfo'}
        onClose={() => setOpenModel({ modelID: '' })}
      />

      <UpgradeSubscription
        open={openModel.modelID === 'upgrade'}
        onClose={() => setOpenModel({ modelID: '' })}
      />

      <ProfileSection
        open={openModel.modelID === 'profile'}
        onClose={() => setOpenModel({ modelID: '' })}
      />

      <WebStoreConfig
        open={openModel.modelID === 'webstoreConfig'}
        onClose={() => setOpenModel({ modelID: '' })}
      />

      <ResetDataDialog
        open={openModel.modelID === 'resetData'}
        onClose={() => setOpenModel({ modelID: '' })}
      />

      <GoalsSection
        open={openModel.modelID === 'goals'}
        onClose={() => setOpenModel({ modelID: '' })}
      />

      {currentMasterType && (
        <MastersDialog
          open={masterDialogOpen}
          onClose={() => {
            setMasterDialogOpen(false);
            setCurrentMasterType(null);
          }}
          masterType={currentMasterType}
        />
      )}

      <OrderStagesDialog
        open={orderStagesOpen}
        onClose={() => setOrderStagesOpen(false)}
      />

      <TktDialog
        isOpen={isFeedbackOpen}
        onClose={handleFeedbackClose}
        selectedFeedbackType={selectedFeedbackType}
      />

      <MobileAlertDrawer
        open={showLogoutAlert}
        onOpenChange={setShowLogoutAlert}
        title="Log out"
        icon={LogOut}
        iconContainerClassName="text-red-700"
        iconClassName="w-5 h-5 text-red-700"
        description="Are you sure you want to log out?"
        description2="You will need to login again to access your account."
        buttonLayout="row"
        primaryButton={{
          label: "Yes, Log out",
          loading: isLoggingOut,
          className: "bg-sahi-red border-sahi-red hover:text-white hover:border-sahi-red text-white",
          onClick: async () => {
            try {
              setIsLoggingOut(true);
              await handleLogout();
            } catch (e) {
              console.error("Logout failed:", e);
            } finally {
              setIsLoggingOut(false);
              setShowLogoutAlert(false);
            }
          }
        }}
        secondaryButton={{
          label: "Cancel",
          variant: "secondary",
          onClick: () => setShowLogoutAlert(false)
        }}
      />

      <AlertBox
        open={showIOSAlert}
        onOpenChange={setShowIOSAlert}
        title="Manage Subscription"
        description="You can manage your subscription by logging into the desktop view."
        actions={[
          {
            label: "OK",
            value: "ok",
            variant: "default",
            className: "bg-sahi-primary-color text-white hover:bg-sahi-primary-color/90"
          }
        ]}
        onAction={() => setShowIOSAlert(false)}
      />
    </>
  );
};

export default Setting;